// Кэш для загруженных данных
const booksCache = new Map(); // Кэш книг по переводам
const chapterCache = new Map(); // Кэш для загруженных глав (ключ: translation-bookId-chapter)
const chapterPending = new Map(); // Дедупликация параллельных запросов одной главы

function chapterCacheKey(translation, bookId, chapter) {
  return `${translation}-${parseInt(bookId, 10)}-${parseInt(chapter, 10)}`;
}

const API_BASE = import.meta.env.VITE_API_URL || 'https://verbum-api-production.up.railway.app/api';

// Загрузка списка книг для конкретного перевода через API
async function loadBooks(translation = 'synodal') {
  // Проверяем кэш
  if (booksCache.has(translation)) {
    return booksCache.get(translation);
  }
  
  try {
    const response = await fetch(`${API_BASE}/bible/${translation}/books`);
    if (!response.ok) {
      throw new Error(`Failed to load books for translation: ${translation}`);
    }
    const data = await response.json();
    const books = data.books || [];
    booksCache.set(translation, books);
    console.log(`✅ Loaded ${books.length} books for translation: ${translation}`);
    return books;
  } catch (error) {
    console.error(`Error loading books for translation ${translation}:`, error);
    return [];
  }
}

// Загрузка главы из API
async function loadChapter(bookId, chapter, translation = 'synodal') {
  const normalizedBookId = parseInt(bookId, 10);
  const normalizedChapter = parseInt(chapter, 10);
  if (!Number.isFinite(normalizedBookId) || !Number.isFinite(normalizedChapter)) return null;

  const cacheKey = chapterCacheKey(translation, normalizedBookId, normalizedChapter);

  if (chapterCache.has(cacheKey)) {
    return chapterCache.get(cacheKey);
  }
  if (chapterPending.has(cacheKey)) {
    return chapterPending.get(cacheKey);
  }

  const task = (async () => {
    try {
      const response = await fetch(
        `${API_BASE}/bible/${translation}/${normalizedBookId}/${normalizedChapter}`
      );
      if (!response.ok) {
        console.warn(
          `Chapter ${normalizedBookId}-${normalizedChapter} not found for translation: ${translation}`
        );
        return null;
      }
      const data = await response.json();
      chapterCache.set(cacheKey, data);
      return data;
    } catch (error) {
      console.error(`Error loading chapter for translation ${translation}:`, error);
      return null;
    } finally {
      chapterPending.delete(cacheKey);
    }
  })();

  chapterPending.set(cacheKey, task);
  return task;
}

function isChapterCached(bookId, chapter, translation = 'synodal') {
  const cacheKey = chapterCacheKey(translation, bookId, chapter);
  return chapterCache.has(cacheKey);
}

/** Синхронно из кэша (для тихого свайпа без повторной подгрузки). */
function getCachedChapterSync(bookId, chapter, translation = 'synodal') {
  const normalizedBookId = parseInt(bookId, 10);
  const normalizedChapter = parseInt(chapter, 10);
  if (!Number.isFinite(normalizedBookId) || !Number.isFinite(normalizedChapter)) return null;

  const cacheKey = chapterCacheKey(translation, normalizedBookId, normalizedChapter);
  const raw = chapterCache.get(cacheKey);
  if (!raw?.verses) return null;

  const books = booksCache.get(translation);
  const book = books?.find((item) => item.id === normalizedBookId);
  const verses = Object.entries(raw.verses)
    .map(([verseNum, text]) => ({
      verse: parseInt(verseNum, 10),
      text,
    }))
    .sort((a, b) => a.verse - b.verse);

  return {
    bookId: normalizedBookId,
    bookName: book?.name,
    chapter: normalizedChapter,
    totalChapters: book?.chapters,
    verses,
  };
}

/** Фоновая подгрузка главы (стихи + параллели) для мгновенного свайпа. */
function prefetchChapter(bookId, chapter, translation = 'synodal') {
  const normalizedBookId = parseInt(bookId, 10);
  const normalizedChapter = parseInt(chapter, 10);
  if (!Number.isFinite(normalizedBookId) || !Number.isFinite(normalizedChapter)) return;
  if (normalizedBookId <= 0 || normalizedChapter <= 0) return;

  const cacheKey = chapterCacheKey(translation, normalizedBookId, normalizedChapter);
  if (chapterCache.has(cacheKey) || chapterPending.has(cacheKey)) return;

  void bibleService.getChapter(normalizedBookId, normalizedChapter, translation).catch(() => {});
}

// Загрузка параллельных мест для главы
async function loadParallelsForChapter(bookId, chapter) {
  try {
    const response = await fetch(`/data/parallels-by-chapter/${bookId}-${chapter}.json`);
    if (!response.ok) {
      // Если файл не найден, значит для этой главы нет параллельных мест
      return null;
    }
    const data = await response.json();
    return data.parallels || {};
  } catch (error) {
    console.error('Error loading parallels:', error);
    return null;
  }
}

// Загрузка текста стиха из загруженной главы
async function loadVerseText(bookId, chapter, verse, translation = 'synodal') {
  const chapterData = await loadChapter(bookId, chapter, translation);
  if (!chapterData || !chapterData.verses) return null;
  
  const verseText = chapterData.verses[String(verse)];
  return verseText || null;
}

// Загрузка всех стихов главы
async function loadChapterVerses(bookId, chapter, translation = 'synodal') {
  const chapterData = await loadChapter(bookId, chapter, translation);
  if (!chapterData || !chapterData.verses) return [];
  
  // Преобразуем объект стихов в массив
  const verses = Object.entries(chapterData.verses)
    .map(([verseNum, text]) => ({
      verse: parseInt(verseNum),
      text: text
    }))
    .sort((a, b) => a.verse - b.verse);
  
  return verses;
}

// Загрузка списка доступных переводов для языка
async function getAvailableTranslations(languageCode) {
  try {
    const response = await fetch(`${API_BASE}/languages`);
    if (!response.ok) {
      return [];
    }
    const data = await response.json();
    const language = data.languages?.find(lang => lang.code === languageCode);
    return language?.translations || [];
  } catch (error) {
    console.error('Error loading available translations:', error);
    return [];
  }
}

export const bibleService = {
  getBooks: async (translation = 'synodal') => {
    return await loadBooks(translation);
  },
  
  getBook: async (bookId, translation = 'synodal') => {
    const books = await bibleService.getBooks(translation);
    const book = books.find(b => b.id === parseInt(bookId));
    return book || null;
  },
  
  // translation: 'synodal' | 'nrt' | 'kjv' | 'niv' | ...
  getChapter: async (bookId, chapter, translation = 'synodal') => {
    const book = await bibleService.getBook(bookId, translation);
    if (!book) return null;
    
    // Загружаем стихи главы нужного перевода
    const verses = await loadChapterVerses(bookId, chapter, translation);
    
    // Загружаем параллельные места для этой главы
    const parallels = await loadParallelsForChapter(bookId, chapter);
    
    // Добавляем информацию о параллельных местах к каждому стиху
    const versesWithParallels = verses.map(verse => ({
      ...verse,
      hasParallels: parallels && parallels[String(verse.verse)] && parallels[String(verse.verse)].length > 0
    }));
    
    return {
      bookId: parseInt(bookId),
      bookName: book.name,
      chapter: parseInt(chapter),
      totalChapters: book.chapters,
      verses: versesWithParallels
    };
  },
  
  getVerse: async (bookId, chapter, verse, translation = 'synodal') => {
    const book = await bibleService.getBook(bookId, translation);
    if (!book) return null;
    
    const verseText = await loadVerseText(bookId, chapter, verse, translation);
    if (!verseText) return null;
    
    return {
      bookId: parseInt(bookId),
      bookName: book.name,
      chapter: parseInt(chapter),
      verse: parseInt(verse),
      text: verseText
    };
  },
  
  getParallelVerses: async (bookId, chapter, verse, translation = 'synodal') => {
    // Загружаем параллельные места для главы
    const parallels = await loadParallelsForChapter(bookId, chapter);
    if (!parallels || !parallels[String(verse)]) {
      return [];
    }
    
    const parallelRefs = parallels[String(verse)];
    
    // Загружаем текст для каждого параллельного места
    const parallelVerses = await Promise.all(
      parallelRefs.map(async (ref) => {
        // Используем bookId напрямую из данных, если он есть
        const refBookId = ref.bookId;
        
        if (!refBookId) {
          console.warn(`bookId не найден для параллельного места: ${ref.book}`);
          return null;
        }
        
        // Загружаем текст стиха из выбранного перевода
        const verseText = await loadVerseText(refBookId, ref.chapter, ref.verse, translation);
        
        return {
          bookId: refBookId,
          bookName: ref.book,
          chapter: ref.chapter,
          verse: ref.verse,
          text: verseText || 'Текст не найден'
        };
      })
    );
    
    // Фильтруем null значения
    return parallelVerses.filter(v => v !== null);
  },
  
  getAvailableTranslations: getAvailableTranslations,

  isChapterCached,

  getCachedChapterSync,

  prefetchChapter,
};
